import { Capacitor } from "@capacitor/core";

let pushPluginPromise = null;

function normalizePermissionState(value, fallback = "prompt") {
  const normalized = String(value || fallback).toLowerCase();
  if (normalized === "granted") {
    return "granted";
  }
  if (normalized === "denied") {
    return "denied";
  }
  if (normalized === "prompt") {
    return "prompt";
  }
  if (normalized === "prompt-with-rationale") {
    return "prompt";
  }
  if (normalized === "prompt_rationale") {
    return "prompt";
  }
  return fallback;
}

export function isNativePushRuntime() {
  try {
    return Boolean(Capacitor?.isNativePlatform?.());
  } catch (error) {
    return false;
  }
}

async function getPushNotificationsPlugin() {
  if (!isNativePushRuntime()) {
    return null;
  }

  if (!pushPluginPromise) {
    pushPluginPromise = import("@capacitor/push-notifications")
      .then((module) => module.PushNotifications || null)
      .catch(() => null);
  }

  return pushPluginPromise;
}

export async function getPushPermissionState() {
  if (isNativePushRuntime()) {
    const plugin = await getPushNotificationsPlugin();
    if (!plugin) {
      return "unsupported";
    }
    try {
      const status = await plugin.checkPermissions();
      return normalizePermissionState(status?.receive, "prompt");
    } catch (error) {
      return "prompt";
    }
  }

  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }

  return normalizePermissionState(window.Notification.permission, "prompt");
}

export async function requestPushPermissionAccess() {
  if (isNativePushRuntime()) {
    const plugin = await getPushNotificationsPlugin();
    if (!plugin) {
      return "unsupported";
    }
    const permission = await plugin.requestPermissions();
    const state = normalizePermissionState(permission?.receive, "prompt");
    if (state === "granted") {
      await plugin.register();
    }
    return state;
  }

  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }

  const result = await window.Notification.requestPermission();
  return normalizePermissionState(result, "prompt");
}

export async function registerForPushNotifications() {
  if (!isNativePushRuntime()) {
    return false;
  }

  const plugin = await getPushNotificationsPlugin();
  if (!plugin) {
    return false;
  }

  await plugin.register();
  return true;
}

export async function registerPushNotifications() {
  if (!isNativePushRuntime()) {
    return "unsupported";
  }

  const permissionState = await requestPushPermissionAccess();
  if (permissionState !== "granted") {
    return permissionState;
  }

  await registerForPushNotifications();
  return "granted";
}

export async function attachPushListeners({
  onToken,
  onNotificationReceived,
  onActionPerformed,
  onRegistrationError,
} = {}) {
  if (!isNativePushRuntime()) {
    return async () => {};
  }

  const plugin = await getPushNotificationsPlugin();
  if (!plugin) {
    return async () => {};
  }

  const handles = await Promise.all([
    plugin.addListener("registration", (token) => {
      const value = token?.value || "";
      if (value) {
        onToken?.(value);
      }
    }),
    plugin.addListener("registrationError", (error) => {
      onRegistrationError?.(error);
    }),
    plugin.addListener("pushNotificationReceived", (notification) => {
      onNotificationReceived?.(notification);
    }),
    plugin.addListener("pushNotificationActionPerformed", (action) => {
      onActionPerformed?.(action?.notification || action);
    }),
  ]);

  return async () => {
    await Promise.all(
      handles.map(async (handle) => {
        try {
          await handle.remove();
        } catch (error) {
          return null;
        }
        return null;
      })
    );
  };
}

export function getNativePushPlatform() {
  if (!isNativePushRuntime()) {
    return "web";
  }
  try {
    return Capacitor.getPlatform?.() || "native";
  } catch (error) {
    return "native";
  }
}
